import {
  Button,
  Input,
  InputRef,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Modal as ModalAntd,
  Typography,
  notification,
} from "antd";
import type { ColumnType, ColumnsType } from "antd/es/table";
import type { FilterConfirmProps } from "antd/es/table/interface";
import {
  DeleteOutlined,
  FileOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { Link, useParams } from "react-router-dom";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import {
  Contract,
  ContractFile,
  ContractFileTable,
  del,
  get,
  put,
} from "../../../../api";
import Highlighter from "react-highlight-words";
import FilesForm from "../detail";
import Modal from "../../../../components/general/modal";
import { CanShow } from "../../../../components";

interface UpdatedFilesListProps {
  contract: Contract;
}

const label = "archivo";

const entries = [
  { key: "contract", label: "Contrato de servicio" },
  { key: "nomina_sua", label: "Nómina - Archivo SUA (.SUA)" },
  { key: "nomina_xml[]", label: "CFDI de Nómina (.XML)" },
  { key: "receipt_nomina", label: "Comprobante de pago Nómina (.PDF)" },
  { key: "list_infonavit", label: "INFONAVIT" },
  { key: "receipt_infonavit", label: "Comprobante de pago INFONAVIT" },
  { key: "opinion_infonavit", label: "Opinión del INFONAVIT" },
  { key: "list_imss", label: "IMSS" },
  { key: "receipt_imss", label: "Comprobante de pago IMSS" },
  { key: "opinion_imss", label: "Opinión del IMSS" },
  { key: "icsoe", label: "ICSOE (cuatrimestral)" },
  { key: "sisub_only", label: "SISUB (cuatrimestral)" },
  {
    key: "tax_federal",
    label: "Declaración Provisional o Definitiva de Impuestos Federales",
  },
  {
    key: "receipt_tax_federal",
    label:
      "Acuse de Recibo Declaración Provisional o Definitiva de Impuestos Federales",
  },
  {
    key: "info_tax_federal",
    label: "Información Registrada de Pagos de Contribuciones Federales",
  },
  { key: "additionals", label: "Archivos adicionales" },
  { key: "additional_opinion", label: "Opinión de cumplimiento" },
  {
    key: "additional_tax_nomina",
    label: "Declaración del Impuesto sobre Nómina",
  },
  {
    key: "additional_receipt_tax_nomina",
    label: "Comprobante de pago del Impuesto sobre Nómina",
  },
];

const independent = [
  "nomina_sua",
  "icsoe",
  "sisub_only",
  "opinion_infonavit",
  "opinion_imss",
  "tax_federal",
  "receipt_tax_federal",
  "info_tax_federal",
  "additional_opinion",
  "additional_tax_nomina",
  "additional_receipt_tax_nomina",
];

const UpdatedFilesList = (p: UpdatedFilesListProps) => {
  const { Text } = Typography;
  const { confirm } = ModalAntd;

  const [innerFiles, setInnerFiles] = useState<ContractFile[]>([]);
  const [files, setFiles] = useState<ContractFileTable[]>([]);
  const [additionalFiles, setAdditionalFiles] = useState<ContractFileTable[]>(
    []
  );
  const [groupFiles, setGroupFiles] = useState<ContractFileTable[]>([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [titleModal, setTitleModal] = useState("");
  const [contextModal, setContextModal] = useState<ReactElement | null>(null);
  const searchInput = useRef<InputRef>(null);
  const [canUpload, setCanUpload] = useState(false);
  const { id } = useParams();

  useEffect(() => {
    setCanUpload(
      files.some((f) => f.key === "nomina_sua" && f.created_at !== undefined)
    );
  }, [files]);

  const buildFile = (entry: any, file?: any): ContractFileTable => ({
    name: file?.name ?? "-",
    name_description: file?.name_description ?? entry.label,
    status: file
      ? file.is_valid
        ? "completo"
        : "por_verificar"
      : "sin_archivo",
    fileUrl: file?.fileUrl ?? "",
    created_at: file?.created_at,
    key: entry.key,
  });

  const setContractTableFiles = (http: { response: any[] }) => {
    const response = http.response;

    setInnerFiles(response);

    const files: ContractFileTable[] = [];
    const additionalFiles: ContractFileTable[] = [];
    const groupFiles: ContractFileTable[] = [];

    const fileMap = new Map(response.map((file) => [file.name_key, file]));

    entries.forEach((entry: any) => {
      if (entry.key === "nomina_xml[]") {
        const filesArray = response.filter((f) => f.name_key === entry.key);
        filesArray.forEach((f) => {
          groupFiles.push(buildFile(entry, f));
        });
        files.push(buildFile(entry, filesArray?.[0] ?? null));
      } else {
        const file = fileMap.get(entry.key);
        const target = entry.key.startsWith("additional_")
          ? additionalFiles
          : files;

        target.push(buildFile(entry, file));
      }
    });

    setFiles(files);
    setAdditionalFiles(additionalFiles);
    setGroupFiles(groupFiles);
  };

  const getFilesCallback = React.useCallback(async () => {
    const http = await get<ContractFile[]>(`/contracts/${id}/files`);
    if (http.statusCode === 200) {
      setContractTableFiles(http);
    }
    setFilesLoading(false);
  }, [id]);

  useEffect(() => {
    if (id !== undefined) getFilesCallback();
  }, [id, getFilesCallback]);

  useEffect(() => {
    getFilesCallback();
  }, [contextModal, getFilesCallback]);

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: keyof ContractFileTable
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex as string);
  };
  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };
  const getColumnSearchProps = (
    dataIndex: keyof ContractFileTable
  ): ColumnType<ContractFileTable> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Buscar`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              handleSearch(selectedKeys as string[], confirm, dataIndex);
            }}
            size="small"
            style={{ width: 90 }}
          >
            Limpiar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Cerrar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex] as string)
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const updateFileData = (
    fileList: ContractFileTable[],
    updatedFiles: ContractFile[]
  ) =>
    fileList.map((f) => {
      const updated = updatedFiles.find((uf) => uf.name_key === f.key);
      return updated
        ? {
            ...f,
            status: updated.is_valid ? "completo" : "por_verificar",
            name: updated.name,
            fileUrl: updated.fileUrl,
            created_at: updated.created_at,
            key: updated.name_key,
          }
        : f;
    });

  const showModal = (
    type: string,
    row: ContractFileTable,
    file?: ContractFile,
    filesArray?: ContractFile[]
  ) => {
    setModalIsVisible(true);
    setTitleModal(
      `${
        type === "add" ? "Agregar" : type === "edit" ? "Editar" : "Ver"
      } ${label}`
    );
    setContextModal(() => (
      <FilesForm
        contract={p.contract}
        row={row}
        type={type}
        file={file}
        files={filesArray}
        add={(e: ContractFile[]) => {
          if (e) {
            setFiles((files) => updateFileData(files, e));
            setAdditionalFiles((files) => updateFileData(files, e));
            setInnerFiles((innerFiles) => [...innerFiles, ...e]);
            setGroupFiles((prev) => {
              const updated = e.filter((f) => f.name_key === "nomina_xml[]");
              if (updated.length === 0) return prev;
              return updated.map((f) => ({
                ...f,
                key: f.name_key,
                status: f.is_valid ? "completo" : "por_verificar",
                name: f.name,
                fileUrl: f.fileUrl,
                created_at: f.created_at,
              }));
            });
            setModalIsVisible(false);
          }
        }}
        update={(e: ContractFile[]) => {
          getFilesCallback();
          setModalIsVisible(false);
        }}
      />
    ));
  };

  const handleDelete = (key: string) => {
    confirm({
      title: `Eliminar ${label}`,
      icon: <ExclamationCircleOutlined />,
      content: `¿Estás seguro que deseas borrar el ${label} seleccionado?, no podrá recuperarlo después.`,
      okText: "Sí",
      okType: "danger",
      cancelText: "No",
      onOk() {
        let fileToDelete = innerFiles.find((f) => f.name_key === key);
        if (fileToDelete !== undefined) {
          del(`/files/${fileToDelete.id}`).then((res) => {
            if (res.statusCode === 200) {
              notification.success({
                message: `Se ha eliminado el ${label} correctamente.`,
              });
              // refresh files
              setFiles((files) =>
                files.map((file) => {
                  if (file.key === key) {
                    return {
                      ...file,
                      status: "sin_archivo",
                      name: "-",
                      fileUrl: "",
                      created_at: undefined,
                    };
                  }
                  return file;
                })
              );

              setAdditionalFiles((files) =>
                files.map((file) => {
                  if (file.key === key) {
                    return {
                      ...file,
                      status: "sin_archivo",
                      name: "-",
                      fileUrl: "",
                      created_at: undefined,
                    };
                  }
                  return file;
                })
              );

              setGroupFiles((prev) => {
                const deleted = prev.some((f) => f.key === key);
                if (!deleted) return prev;
                return [];
              });
            }
          });
        }
      },
      onCancel() {},
    });
  };

  const handleVerification = (key: string, isValid: 0 | 1) => {
    confirm({
      title: !isValid ? `Verificar ${label}` : `Invalidar ${label}`,
      icon: <ExclamationCircleOutlined />,
      content: !isValid
        ? `¿Estás seguro que deseas verificar el ${label} seleccionado?, no se podrá modificar después.`
        : `¿Estás seguro que deseas marcar como inválido el ${label} seleccionado?`,
      okText: "Sí",
      okType: "danger",
      cancelText: "No",
      onOk() {
        const fileToVerify = innerFiles.find((f) => f.name_key === key);

        if (fileToVerify) {
          put(`/files/${fileToVerify.id}`, { is_valid: !isValid }).then(
            (res) => {
              if (res.statusCode === 200) {
                notification.success({
                  message: !isValid
                    ? `Se ha verificado el ${label} correctamente.`
                    : `El ${label} se ha marcado como inválido.`,
                });
                getFilesCallback();
              }
            }
          );
        }
      },
      onCancel() {},
    });
  };

  const modalCallback = React.useCallback(
    (file: any, record: ContractFileTable) => {
      get<ContractFile[]>(`/contracts/${id}/files`).then((e) => {
        const resp = e.response;
        const algo = resp.filter((f) => f.name_key === record.key);
        showModal("edit", file, algo[0], algo.length > 0 ? algo : undefined);
      });
    },
    [id]
  );

  const columns: ColumnsType<ContractFileTable> = [
    {
      title: "Nombre",
      dataIndex: "name_description",
      key: "name_description",
      ...getColumnSearchProps("name_description"),
      render: (name_description) => <Text>{name_description}</Text>,
    },
    {
      title: "Estatus",
      key: "status",
      dataIndex: "status",
      filters: [
        { text: "SIN ARCHIVO", value: "sin_archivo" },
        { text: "POR VERIFICAR", value: "por_verificar" },
        { text: "COMPLETO", value: "completo" },
      ],
      onFilter: (value, record) => record.status.startsWith(value as string),
      render: (status: string, record) => {
        if (record.key === "additionals") return null;
        return (
          <Row gutter={16} style={{ width: 190 }}>
            <Tag
              color={
                status === "completo"
                  ? "green"
                  : status === "por_verificar"
                  ? "orange"
                  : "red"
              }
              key={status}
              style={{ marginBottom: 10 }}
            >
              {status.replace("_", " ").toUpperCase()}
            </Tag>
          </Row>
        );
      },
    },
    {
      title: "Archivo",
      dataIndex: "fileUrl",
      key: "fileUrl",
      ...getColumnSearchProps("fileUrl"),
      render: (fileUrl, rec) =>
        rec.key === "additionals" ||
        rec.key === "nomina_xml[]" ? null : fileUrl ? (
          <Link to={fileUrl} target="_blank">
            {rec.name}
          </Link>
        ) : (
          "-"
        ),
    },
    {
      title: "Fecha de creación",
      dataIndex: "created_at",
      key: "created_at",
      render: (created_at, record) =>
        record.key === "additionals" || record.key === "nomina_xml[]"
          ? null
          : created_at
          ? moment.unix(created_at).format("DD/MM/YYYY")
          : "-",
    },
    {
      title: "Acciones",
      key: "sup",
      fixed: "right",
      width: 170,
      align: "center",
      render: (file, record) => {
        if (record.key === "additionals") return null;
        return (
          <>
            {record.status === "sin_archivo" && (
              <CanShow roles={["Supplier"]}>
                <Tooltip title="Cargar archivo">
                  <Button
                    style={{
                      marginLeft: 10,
                      backgroundColor: "#1890ff",
                      borderColor: "#1890ff",
                    }}
                    type="primary"
                    shape="default"
                    size="small"
                    icon={<FileOutlined />}
                    onClick={() => {
                      showModal("add", file);
                    }}
                  />
                </Tooltip>
              </CanShow>
            )}

            {record.status === "por_verificar" && record.fileUrl && (
              <CanShow roles={["Supplier"]}>
                <Tooltip title="Editar archivo">
                  <Button
                    style={{
                      marginLeft: 10,
                    }}
                    ghost
                    type="primary"
                    shape="default"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => modalCallback(file, record)}
                  />
                </Tooltip>
              </CanShow>
            )}

            {record.status === "completo" && (
              <>
                <CanShow roles={["Admin", "Financial Advisor"]}>
                  <Tooltip title="Cambiar estatus">
                    <Button
                      style={{
                        marginLeft: 10,
                      }}
                      color="danger"
                      variant="solid"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() =>
                        handleVerification(
                          record.key,
                          record.status === "completo" ? 1 : 0
                        )
                      }
                    />
                  </Tooltip>
                </CanShow>
                <Tooltip title="Completo">
                  <Button
                    style={{
                      marginLeft: 10,
                      backgroundColor: "#68d78a",
                      borderColor: "#68d78a",
                    }}
                    type="primary"
                    shape="default"
                    size="small"
                    icon={<CheckCircleOutlined />}
                  />
                </Tooltip>
              </>
            )}
            {record.status === "por_verificar" && (
              <CanShow roles={["Admin", "Financial Advisor"]}>
                <Tooltip title="Verificar">
                  <Button
                    style={{
                      marginLeft: 10,
                    }}
                    type="primary"
                    shape="default"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={() =>
                      handleVerification(
                        record.key,
                        record.status === "completo" ? 1 : 0
                      )
                    }
                  />
                </Tooltip>
              </CanShow>
            )}
            {record.name !== "-" && record.status !== "completo" && (
              <Tooltip title="Eliminar">
                <Button
                  danger
                  style={{
                    marginLeft: 10,
                  }}
                  type="primary"
                  shape="default"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.key)}
                />
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  const additionalColumns: ColumnsType<ContractFileTable> = [
    {
      title: "Nombre",
      dataIndex: "name_description",
      key: "name_description",
      render: (name_description) => <Text>{name_description}</Text>,
    },
    {
      title: "Estatus",
      key: "status",
      dataIndex: "status",
      render: (status: string, record) => {
        return (
          <Row gutter={16} style={{ width: 190 }}>
            <Tag
              color={
                status === "completo"
                  ? "green"
                  : status === "por_verificar"
                  ? "orange"
                  : "red"
              }
              key={status}
              style={{ marginBottom: 10 }}
            >
              {status.replace("_", " ").toUpperCase()}
            </Tag>
          </Row>
        );
      },
    },
    {
      title: "Archivo",
      dataIndex: "fileUrl",
      key: "fileUrl",
      render: (fileUrl, rec) => (
        <Link to={fileUrl} target="_blank">
          {rec.name}
        </Link>
      ),
    },
    {
      title: "Fecha de creación",
      dataIndex: "created_at",
      key: "created_at",
      render: (created_at) => (
        <>{created_at ? moment.unix(created_at).format("DD/MM/YYYY") : "-"}</>
      ),
    },
    {
      title: "Acciones",
      key: "sup",
      fixed: "right",
      width: 170,
      align: "center",
      render: (file, record) => {
        return (
          <>
            {record.status === "sin_archivo" && (
              <CanShow roles={["Supplier"]}>
                <Tooltip title="Cargar archivo">
                  <Button
                    style={{
                      marginLeft: 10,
                      backgroundColor: "#1890ff",
                      borderColor: "#1890ff",
                    }}
                    type="primary"
                    shape="default"
                    size="small"
                    icon={<FileOutlined />}
                    onClick={() => {
                      showModal("add", file);
                    }}
                  />
                </Tooltip>
              </CanShow>
            )}
            {record.status === "por_verificar" && (
              <CanShow roles={["Supplier"]}>
                <Tooltip title="Editar archivo">
                  <Button
                    style={{
                      marginLeft: 10,
                    }}
                    ghost
                    type="primary"
                    shape="default"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => modalCallback(file, record)}
                  />
                </Tooltip>
              </CanShow>
            )}
            {record.status === "completo" && (
              <>
                <CanShow roles={["Admin", "Financial Advisor"]}>
                  <Tooltip title="Cambiar estatus">
                    <Button
                      style={{
                        marginLeft: 10,
                      }}
                      color="danger"
                      variant="solid"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() =>
                        handleVerification(
                          record.key,
                          record.status === "completo" ? 1 : 0
                        )
                      }
                    />
                  </Tooltip>
                </CanShow>
                <Tooltip title="Completo">
                  <Button
                    style={{
                      marginLeft: 10,
                      backgroundColor: "#68d78a",
                      borderColor: "#68d78a",
                    }}
                    type="primary"
                    shape="default"
                    size="small"
                    icon={<CheckCircleOutlined />}
                  />
                </Tooltip>
              </>
            )}
            {record.status === "por_verificar" && (
              <CanShow roles={["Admin", "Financial Advisor"]}>
                <Tooltip title="Verificar">
                  <Button
                    style={{
                      marginLeft: 10,
                    }}
                    type="primary"
                    shape="default"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={() =>
                      handleVerification(
                        record.key,
                        record.status === "completo" ? 1 : 0
                      )
                    }
                  />
                </Tooltip>
              </CanShow>
            )}
            {record.name !== "-" && record.status !== "completo" && (
              <Tooltip title="Eliminar">
                <Button
                  danger
                  style={{
                    marginLeft: 10,
                  }}
                  type="primary"
                  shape="default"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.key)}
                />
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  const groupColumns: ColumnsType<ContractFileTable> = [
    {
      title: "Nombre",
      dataIndex: "name_description",
      key: "name_description",
      render: (name_description) => <Text>{name_description}</Text>,
    },
    {
      title: "Estatus",
      key: "status",
      dataIndex: "status",
      render: (status: string, record) => {
        return (
          <Row gutter={16} style={{ width: 190 }}>
            <Tag
              color={
                status === "completo"
                  ? "green"
                  : status === "por_verificar"
                  ? "orange"
                  : "red"
              }
              key={status}
              style={{ marginBottom: 10 }}
            >
              {status.replace("_", " ").toUpperCase()}
            </Tag>
          </Row>
        );
      },
    },
    {
      title: "Archivo",
      dataIndex: "fileUrl",
      key: "fileUrl",
      render: (fileUrl, rec) => (
        <Link to={fileUrl} target="_blank">
          {rec.name}
        </Link>
      ),
    },
    {
      title: "Fecha de creación",
      dataIndex: "created_at",
      key: "created_at",
      render: (created_at) => (
        <>{created_at ? moment.unix(created_at).format("DD/MM/YYYY") : "-"}</>
      ),
    },
  ];

  const expandedRowRender = (record: ContractFileTable) => {
    if (record.key === "additionals") {
      return (
        <Table
          columns={additionalColumns}
          dataSource={additionalFiles}
          pagination={false}
          scroll={{ x: 1000 }}
          rowKey="key"
          size="small"
        />
      );
    }

    if (record.key === "nomina_xml[]") {
      return (
        <Table
          columns={groupColumns}
          dataSource={groupFiles}
          pagination={false}
          scroll={{ x: 1000 }}
          rowKey="key"
          size="small"
        />
      );
    }
    return null;
  };

  return (
    <Spin spinning={filesLoading} tip={<p>Cargando...</p>}>
      {/* Tabla */}
      {!filesLoading ? (
        <Table
          columns={columns}
          dataSource={files}
          scroll={{ x: 1000 }}
          pagination={false}
          expandable={{
            expandedRowRender,
            rowExpandable: (record) =>
              record.key === "additionals" || record.key === "nomina_xml[]",
          }}
        />
      ) : null}
      {/* Modal de detalle */}
      <Modal
        title={titleModal}
        isVisible={modalIsVisible}
        setIsVisible={setModalIsVisible}
        width={1000}
      >
        {contextModal}
      </Modal>
    </Spin>
  );
};

export default UpdatedFilesList;
